// ════════════════════════════════════════════════════════════════════════════
// AltLinksTab — ligar cuentas para usar alters como bots (mod-playerbots).
//
// Cada cuenta puede invocar como bots sus propios personajes; con un ligado, también
// los de la otra cuenta. La solicitud la envía el dueño de una cuenta y la acepta el
// de la otra, cada uno con su sesión web. Cualquiera de los dos puede deshacerlo.
// Backend: backend/account_links_router.py · Diseño: ~/docs/playerbots_ligado_cuentas.md
// ════════════════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Send, CheckCircle, AlertCircle, X, Unlink, Clock, AlertTriangle } from 'lucide-react';
import { useConfirm } from '../admin/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;
const API = `${BACKEND_URL}/api/account/alt-links`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const errMsg = (err, fallback) => err?.response?.data?.detail || fallback;

const SENT_STATUS = {
  pendiente:   { label: 'Pendiente',   color: '#facc15' },
  no_aceptada: { label: 'No aceptada', color: 'rgba(224,247,255,0.45)' },
  aceptada:    { label: 'Aceptada',    color: '#4ade80' },
  deshecha:    { label: 'Deshecha',    color: 'rgba(224,247,255,0.45)' },
  cancelada:   { label: 'Cancelada',   color: 'rgba(224,247,255,0.45)' },
};

// Lo que implica aceptar: comprobado en mod-playerbots (ver el doc de diseño, §2).
export function AltLinkExplanation({ account }) {
  const who = account ? <strong style={{ color: '#fff' }}>«{account}»</strong> : 'la otra cuenta';
  return (
    <div style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(224,247,255,0.75)' }}>
      <p style={{ margin: '0 0 8px' }}>
        Si aceptas, las dos cuentas podréis invocar como bots los personajes de la otra
        (con <code>.playerbots bot add &lt;nombre&gt;</code> o desde MultiBot, añadiéndolos por su nombre).
      </p>
      <p style={{ margin: '0 0 8px' }}>
        Mientras tenga uno de tus personajes como bot, {who} podrá hacer con él <strong style={{ color: '#fff' }}>lo
        mismo que con los suyos</strong>: llevarlo a mazmorras, bandas y campos de batalla, cambiarle el equipo
        y los talentos, y también <strong style={{ color: '#f87171' }}>pedirle por comercio o por correo tu oro y
        tus objetos, venderlos o destruirlos</strong>.
      </p>
      <p style={{ margin: '0 0 8px' }}>
        Acepta solo si confías en esa persona como para prestarle la cuenta.
      </p>
      <p style={{ margin: 0, color: 'rgba(224,247,255,0.55)' }}>
        No afecta a tu tienda, cristales, compras ni a nada de la web, y puedes deshacerlo cuando quieras sin
        pedir permiso. No entres con un personaje mientras la otra cuenta lo tenga invocado como bot:
        pídele antes que lo despida.
      </p>
    </div>
  );
}

const box = {
  background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12, padding: '16px 18px',
};
const sectionTitle = {
  display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px',
  fontSize: 12, fontWeight: 700, letterSpacing: 1.2, color: 'rgba(224,247,255,0.6)', textTransform: 'uppercase',
};
const rowStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
  padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.05)',
};
const smallBtn = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
  fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'transparent',
  border: `1px solid ${color}55`, color,
});

function Feedback({ status }) {
  if (!status) return null;
  return (
    <div className={`cuenta-pass-feedback ${status.ok ? 'cuenta-pass-feedback--ok' : 'cuenta-pass-feedback--err'}`}
         style={{ marginTop: 10 }}>
      {status.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
      {status.msg}
    </div>
  );
}

export default function AltLinksTab({ onReceivedChange }) {
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [target, setTarget]   = useState('');
  const [sending, setSending] = useState(false);
  const [busy, setBusy]       = useState(null);
  const [status, setStatus]   = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const load = useCallback(() => (
    axios.get(API, { withCredentials: true })
      .then(({ data: d }) => {
        setData(d);
        setError(null);
        onReceivedChange?.(d.received.map((r) => r.account));
      })
      .catch((err) => setError(errMsg(err, 'No se pudo cargar el ligado de cuentas.')))
  ), [onReceivedChange]);

  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    setStatus(null);
    try {
      const { data: d } = await fn();
      setStatus({ ok: true, msg: okMsg || d.message });
      await load();
    } catch (err) {
      setStatus({ ok: false, msg: errMsg(err, 'No se pudo completar la acción') });
    } finally {
      setBusy(null);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const account = target.trim();
    if (!account) return;
    setSending(true);
    setStatus(null);
    try {
      const { data: d } = await axios.post(`${API}/requests`, { account }, { withCredentials: true });
      setStatus({ ok: true, msg: d.message });
      setTarget('');
      await load();
    } catch (err) {
      setStatus({ ok: false, msg: errMsg(err, 'No se pudo enviar la solicitud') });
    } finally {
      setSending(false);
    }
  };

  const handleAccept = async (req) => {
    const ok = await confirm({
      title: 'Aceptar ligado de cuentas',
      body: <AltLinkExplanation account={req.account} />,
      confirmLabel: `Ligar con ${req.account}`,
      tone: 'danger',
    });
    if (!ok) return;
    act(`acc-${req.id}`, () => axios.post(`${API}/requests/${req.id}/accept`, {}, { withCredentials: true }));
  };

  const handleUnlink = async (link) => {
    const ok = await confirm({
      title: 'Deshacer ligado',
      body: (
        <>
          Dejaréis de poder invocar los personajes de la otra cuenta: <strong>«{link.account}»</strong> y tú.
          Los que estén invocados ahora mismo siguen hasta que se despidan o su dueño salga del juego.
        </>
      ),
      confirmLabel: 'Deshacer',
      tone: 'danger',
    });
    if (!ok) return;
    act(`del-${link.account}`, () => axios.delete(`${API}/${encodeURIComponent(link.account)}`, { withCredentials: true }));
  };

  if (error && !data) return <p className="cuenta-tab-empty">{error}</p>;
  if (!data) return <p className="cuenta-tab-empty">Cargando...</p>;

  const { sent, received, active, limits } = data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {confirmDialog}

      <div style={{ ...box, background: 'rgba(0,229,255,0.04)', borderColor: 'rgba(0,229,255,0.15)' }}>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'rgba(224,247,255,0.75)' }}>
          Cada cuenta puede usar como bots sus propios personajes. Si ligas tu cuenta con la de otra persona,
          las dos podréis invocar también los personajes de la otra. Hace falta que ella acepte desde su cuenta,
          y cualquiera de las dos puede deshacerlo. Solo sirve para los bots del juego: no cambia nada de la
          tienda ni de la web.
        </p>
      </div>

      {received.length > 0 && (
        <div style={{ ...box, borderColor: 'rgba(250,204,21,0.35)', background: 'rgba(250,204,21,0.05)' }}
             data-testid="altlinks-received">
          <p style={sectionTitle}><AlertTriangle size={14} style={{ color: '#facc15' }} /> Solicitudes recibidas</p>
          {received.map((req) => (
            <div key={req.id} style={{ ...rowStyle, flexDirection: 'column', alignItems: 'stretch' }}>
              <p style={{ margin: '0 0 8px', color: '#fff', fontWeight: 700 }}>
                La cuenta «{req.account}» quiere ligarse contigo
                <span style={{ fontWeight: 400, fontSize: 12, color: 'rgba(224,247,255,0.45)', marginLeft: 8 }}>
                  caduca el {fmtDate(req.expires_at)}
                </span>
              </p>
              <AltLinkExplanation account={req.account} />
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <button type="button" style={smallBtn('#4ade80')} disabled={!!busy}
                        onClick={() => handleAccept(req)}>
                  <CheckCircle size={13} /> Aceptar
                </button>
                <button type="button" style={smallBtn('#f87171')} disabled={!!busy}
                        onClick={() => act(`rej-${req.id}`,
                          () => axios.post(`${API}/requests/${req.id}/reject`, {}, { withCredentials: true }))}>
                  <X size={13} /> Rechazar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={box}>
        <p style={sectionTitle}><Send size={14} style={{ color: '#00e5ff' }} /> Ligar una cuenta</p>
        <form onSubmit={handleSend} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="text" className="form-input" style={{ flex: 1, minWidth: 180 }}
            placeholder="Nombre de la cuenta de juego"
            value={target} maxLength={16} autoComplete="off" spellCheck={false}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="altlinks-target"
          />
          <button type="submit" className="btn-primary" disabled={sending || !target.trim()}>
            {sending ? 'Enviando...' : 'Enviar solicitud'}
          </button>
        </form>
        <p style={{ margin: '10px 0 0', fontSize: 12, lineHeight: 1.5, color: 'rgba(224,247,255,0.45)' }}>
          Es el nombre con el que la otra persona entra en el juego, no el de un personaje. Verá el nombre
          de tu cuenta. Máximo {limits.max_links} cuentas ligadas, {limits.daily_requests} solicitudes al día
          y {limits.max_pending} pendientes a la vez; caducan a los {limits.expiry_days} días.
        </p>
        <Feedback status={status} />
      </div>

      <div style={box}>
        <p style={sectionTitle}><Users size={14} style={{ color: '#00e5ff' }} /> Cuentas ligadas</p>
        {active.length === 0 && <p style={{ margin: 0, fontSize: 13, color: 'rgba(224,247,255,0.45)' }}>Ninguna.</p>}
        {active.map((link) => (
          <div key={link.account} style={rowStyle}>
            <div>
              <span style={{ color: '#fff', fontWeight: 700, fontFamily: 'monospace' }}>{link.account}</span>
              <span style={{ fontSize: 12, color: 'rgba(224,247,255,0.45)', marginLeft: 10 }}>
                {link.origin === 'juego' ? 'creado en el juego' : `desde el ${fmtDate(link.since)}`}
                {link.i_can_use && !link.they_can_use && ' · solo tú usas sus personajes'}
                {!link.i_can_use && link.they_can_use && ' · solo esa cuenta usa los tuyos'}
              </span>
            </div>
            <button type="button" style={smallBtn('#f87171')} disabled={!!busy} onClick={() => handleUnlink(link)}>
              <Unlink size={13} /> Deshacer
            </button>
          </div>
        ))}
      </div>

      {sent.length > 0 && (
        <div style={box}>
          <p style={sectionTitle}><Clock size={14} style={{ color: '#00e5ff' }} /> Solicitudes enviadas</p>
          {sent.map((req) => {
            const st = SENT_STATUS[req.status] || SENT_STATUS.pendiente;
            return (
              <div key={req.id} style={rowStyle}>
                <div>
                  <span style={{ color: '#fff', fontWeight: 700, fontFamily: 'monospace' }}>{req.account}</span>
                  <span style={{ fontSize: 12, color: st.color, marginLeft: 10 }}>{st.label}</span>
                  <span style={{ fontSize: 12, color: 'rgba(224,247,255,0.35)', marginLeft: 10 }}>
                    enviada el {fmtDate(req.created_at)}
                  </span>
                </div>
                {req.status === 'pendiente' && (
                  <button type="button" style={smallBtn('rgba(224,247,255,0.6)')} disabled={!!busy}
                          onClick={() => act(`can-${req.id}`,
                            () => axios.post(`${API}/requests/${req.id}/cancel`, {}, { withCredentials: true }))}>
                    <X size={13} /> Cancelar
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
